import { GET_CHAT_BY_ID } from '@/hooks/query/useQueryChatById';
import { GET_CHATS } from '@/hooks/query/useQueryChats';
import { invalidateSavedChats } from '@/hooks/query/useQuerySavedChats';
import { bulkDeleteChats, type BulkDeleteChatsResponse } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import type { ChatListResponse } from '@heidi/session/contracts';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

interface BulkDeleteContext {
  previousInfiniteQueries: [
    readonly unknown[],
    InfiniteData<ChatListResponse, number> | undefined,
  ][];
  previousFlatQueries: [readonly unknown[], ChatListResponse | undefined][];
}

function filterPage(page: ChatListResponse, idsToDelete: Set<string>): ChatListResponse {
  const remaining = page.chats.filter((c) => !idsToDelete.has(c.id));
  return {
    ...page,
    chats: remaining,
    total: page.total - (page.chats.length - remaining.length),
  };
}

const isFlatChatsQueryKey = (queryKey: readonly unknown[]) =>
  queryKey[0] === GET_CHATS && queryKey[1] !== 'infinite';

export function useMutateBulkDeleteChats() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();

  return useMutation<BulkDeleteChatsResponse, Error, string[], BulkDeleteContext>({
    mutationFn: async (ids: string[]) => {
      const { data } = await bulkDeleteChats({ ids });
      return data;
    },
    onMutate: (ids: string[]) => {
      const idsToDelete = new Set(ids);

      const previousInfiniteQueries = queryClient.getQueriesData<
        InfiniteData<ChatListResponse, number>
      >({ queryKey: [GET_CHATS, 'infinite'] });

      queryClient.setQueriesData<InfiniteData<ChatListResponse, number>>(
        { queryKey: [GET_CHATS, 'infinite'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => filterPage(page, idsToDelete)),
          };
        }
      );

      const previousFlatQueries = queryClient.getQueriesData<ChatListResponse>({
        predicate: (query) => isFlatChatsQueryKey(query.queryKey),
      });
      queryClient.setQueriesData<ChatListResponse>(
        { predicate: (query) => isFlatChatsQueryKey(query.queryKey) },
        (old) => {
          if (!old) return old;
          return filterPage(old, idsToDelete);
        }
      );

      return { previousInfiniteQueries, previousFlatQueries };
    },
    onError: (error, _ids, context) => {
      logger.error({ error });

      if (context?.previousInfiniteQueries) {
        for (const [key, data] of context.previousInfiniteQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousFlatQueries) {
        for (const [key, data] of context.previousFlatQueries) {
          queryClient.setQueryData(key, data);
        }
      }

      toast({
        title: t('chat.deleteErrorTitle'),
        description: t('chat.deleteErrorDescription'),
        tone: 'destructive',
      });
    },
    onSettled: (_data, _error, ids) => {
      void queryClient.invalidateQueries({ queryKey: [GET_CHATS] });
      void invalidateSavedChats();
      for (const id of ids) {
        void queryClient.invalidateQueries({ queryKey: [GET_CHAT_BY_ID, id] });
      }
    },
  });
}
